/**
 * FP3-044 Phase A — Admin Services & Fidelity tab (read-only).
 *
 * Surfaces the cross-provider fidelity rollup + per-service summary.
 * MVP is read-only. Commitment editing (minutes/week, delivery
 * pattern) remains in Service Demographics per the spec §12 (MVP+1).
 */
import { Card } from "@/components/ui/card";

const STATUS_LABEL = {
  met: "Met",
  over: "Over target",
  on_track: "On track",
  behind: "Behind",
  no_data: "No data",
  met_after_exclusions: "Met (after excluded days)",
  needs_makeup: "Needs make-up",
};

const STATUS_STYLE = {
  met: "bg-[#E8F2EE] text-[#2F6B53]",
  over: "bg-[#EEE9F7] text-[#5A4B85]",
  on_track: "bg-[#F3F4F6] text-[#4B5563]",
  behind: "bg-[#FBEEEE] text-[#A8453A]",
  no_data: "bg-[#F3F1EC] text-[#6B7280]",
  met_after_exclusions: "bg-[#E8F2EE] text-[#2F6B53]",
  needs_makeup: "bg-[#FDF8F3] text-[#9C6B2E]",
};

export default function AdminServicesTab({
  rollup, commitments = [], services = [], compactPreview = false,
}) {
  const byService = rollup?.by_service || [];
  const byProvider = rollup?.by_provider || [];

  return (
    <Card className="p-5 bg-white border-[#E5E3DB]" data-testid="admin-services-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[13px] font-medium text-[#1F2937]">
          Services & fidelity{rollup ? "" : " · loading"}
        </p>
      </div>

      {byService.length === 0 ? (
        <p className="text-[12px] text-[#6B7280] italic" data-testid="admin-services-empty">
          No commitments yet for this student.
        </p>
      ) : (
        <div className="space-y-3">
          {(compactPreview ? byService.slice(0, 3) : byService).map((s) => (
            <div
              key={s.service_type}
              data-testid={`admin-services-row-${s.service_type}`}
              className="border border-[#E5E3DB] rounded-lg p-3"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-[12px] font-medium text-[#1F2937]">
                    {s.label || s.service_type}
                  </p>
                  <p className="text-[11px] text-[#6B7280]">
                    {s.delivered_minutes ?? 0} / {s.adjusted_target_minutes || s.target_minutes || 0} min
                    {s.providers?.length > 1 ? ` · ${s.providers.length} providers` : ""}
                    {s.make_up_needed_minutes ? ` · ${s.make_up_needed_minutes} make-up min` : ""}
                  </p>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[s.status] || STATUS_STYLE.on_track}`}
                  data-testid={`admin-services-status-${s.service_type}`}
                >
                  {STATUS_LABEL[s.status] || s.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!compactPreview && byProvider.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] font-medium text-[#6B7280] mb-2">Per-provider rollup</p>
          <div className="space-y-1.5">
            {byProvider.map((p) => (
              <div
                key={p.provider_id}
                data-testid={`admin-services-provider-${p.provider_id}`}
                className="flex items-center justify-between border border-[#E5E3DB] rounded-md px-3 py-1.5"
              >
                <p className="text-[12px] text-[#1F2937]">
                  {p.provider_first_name} {p.provider_last_name}
                  {p.provider_role ? <span className="text-[10px] text-[#6B7280] ml-1.5">· {p.provider_role}</span> : null}
                </p>
                <span
                  className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[p.status_rollup] || STATUS_STYLE.on_track}`}
                >
                  {STATUS_LABEL[p.status_rollup] || p.status_rollup}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {compactPreview && byService.length > 3 && (
        <p className="mt-3 text-[11px] text-[#6B7280]">
          Showing 3 of {byService.length}. Open the Services tab for the full picture.
        </p>
      )}

      <p className="mt-4 text-[10.5px] text-[#9B9890] italic">
        Service commitment editing remains in Service Demographics (admin MVP+1).
      </p>
    </Card>
  );
}
